// Platform-wide Instagram reconnect banner.
//
// Mounted ONCE at the root of App.js (above the Router). Listens for
// three custom window events:
//
//   ig_token_expired         → red persistent banner (no dismiss)
//   ig_token_expiring_soon   → yellow dismissible banner; payload {days}
//   ig_webhook_unsubscribed  → yellow banner with self-heal CTA. Fired
//                              by useInstagramStatus when Meta has
//                              silently dropped the per-user webhook
//                              subscription. CTA calls
//                              POST /api/instagram/resubscribe.
//
// Events are fired by:
//   - The axios response interceptor in App.js (whenever any API call
//     returns 401 with detail.code='ig_token_invalid').
//   - The useInstagramStatus hook, which calls /api/instagram/status
//     on every page that renders Instagram-data.
//
// The banner uses fixed-top positioning + z-index above all content
// (z-[9999]). Lucide icons (no emoji per design guidelines).
import { useEffect, useState } from "react";
import { AlertTriangle, Clock, X, Webhook, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "../App";

export default function InstagramReconnectBanner() {
  const [expired, setExpired] = useState(false);
  const [expiredMsg, setExpiredMsg] = useState("");
  const [expiring, setExpiring] = useState(false);
  const [daysLeft, setDaysLeft] = useState(null);

  // Webhook self-heal banner state (Apr 2026)
  const [webhookOff, setWebhookOff] = useState(false);
  const [healing, setHealing] = useState(false);
  const [healed, setHealed] = useState(false);
  const [healError, setHealError] = useState("");

  useEffect(() => {
    const onExpired = (e) => {
      setExpiredMsg((e?.detail?.message) || "");
      setExpired(true);
    };
    const onExpiring = (e) => {
      const d = e?.detail?.days;
      if (typeof d === "number" && d >= 0) {
        setDaysLeft(d);
        setExpiring(true);
      }
    };
    const onWebhookOff = () => {
      setWebhookOff(true);
      setHealed(false);
      setHealError("");
    };
    // Allow other parts of the app (eg. the reconnect success page) to
    // hide the banner without a reload.
    const onCleared = () => {
      setExpired(false);
      setExpiring(false);
      setDaysLeft(null);
      setWebhookOff(false);
    };
    window.addEventListener("ig_token_expired", onExpired);
    window.addEventListener("ig_token_expiring_soon", onExpiring);
    window.addEventListener("ig_webhook_unsubscribed", onWebhookOff);
    window.addEventListener("ig_token_cleared", onCleared);
    return () => {
      window.removeEventListener("ig_token_expired", onExpired);
      window.removeEventListener("ig_token_expiring_soon", onExpiring);
      window.removeEventListener("ig_webhook_unsubscribed", onWebhookOff);
      window.removeEventListener("ig_token_cleared", onCleared);
    };
  }, []);

  const handleResubscribe = async () => {
    if (healing) return;
    setHealing(true);
    setHealError("");
    try {
      const r = await api.post("/instagram/resubscribe");
      if (r?.data?.ok) {
        setHealed(true);
        // Hide the banner after a short success flash.
        setTimeout(() => setWebhookOff(false), 2000);
      } else {
        setHealError("Couldn't reconnect — please contact support.");
      }
    } catch {
      setHealError("Couldn't reconnect — please contact support.");
    } finally {
      setHealing(false);
    }
  };

  if (expired) {
    return (
      <div
        data-testid="ig-reconnect-banner-expired"
        className="fixed top-0 left-0 right-0 z-[9999] bg-[#E24B4A] text-white shadow-lg"
        role="alert"
      >
        <div className="max-w-screen-2xl mx-auto px-5 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <AlertTriangle className="w-5 h-5 shrink-0" />
            <div className="min-w-0">
              <div className="font-semibold text-sm">
                Instagram connection expired
              </div>
              <div className="text-xs opacity-90 truncate">
                {expiredMsg || "Your automations are paused. Reconnect to resume."}
              </div>
            </div>
          </div>
          <a
            href="/settings/instagram"
            data-testid="ig-reconnect-banner-cta"
            className="bg-white text-[#E24B4A] px-4 py-2 rounded-md font-semibold text-xs whitespace-nowrap hover:bg-white/90 transition shrink-0"
          >
            Reconnect Instagram →
          </a>
        </div>
      </div>
    );
  }

  if (expiring && daysLeft !== null) {
    return (
      <div
        data-testid="ig-reconnect-banner-expiring"
        className="fixed top-0 left-0 right-0 z-[9998] bg-[#EF9F27] text-white"
        role="alert"
      >
        <div className="max-w-screen-2xl mx-auto px-5 py-2.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Clock className="w-4 h-4 shrink-0" />
            <span className="text-sm truncate">
              Your Instagram connection expires in{" "}
              <strong>{daysLeft} day{daysLeft === 1 ? "" : "s"}</strong>.
              Reconnect now to avoid interruption.
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <a
              href="/settings/instagram"
              data-testid="ig-reconnect-banner-expiring-cta"
              className="bg-white text-[#EF9F27] px-3 py-1.5 rounded-md font-semibold text-xs whitespace-nowrap hover:bg-white/90 transition"
            >
              Reconnect →
            </a>
            <button
              type="button"
              onClick={() => setExpiring(false)}
              data-testid="ig-reconnect-banner-expiring-dismiss"
              aria-label="Dismiss"
              className="p-1 rounded hover:bg-white/15 transition"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Webhook subscription dropped — yellow self-heal banner. Suppressed
  // when token-expired/expiring banners are already up so the user
  // never sees two stacked banners.
  if (webhookOff) {
    return (
      <div
        data-testid="ig-webhook-banner"
        className="fixed top-0 left-0 right-0 z-[9997] bg-[#EF9F27] text-white"
        role="alert"
      >
        <div className="max-w-screen-2xl mx-auto px-5 py-2.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {healed
              ? <CheckCircle2 className="w-4 h-4 shrink-0" />
              : <Webhook className="w-4 h-4 shrink-0" />}
            <span className="text-sm truncate">
              {healed
                ? "Reconnected — your automations are receiving Instagram triggers again."
                : (healError
                    ? healError
                    : "Your automations may not be receiving Instagram triggers.")}
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {!healed && !healError && (
              <button
                type="button"
                onClick={handleResubscribe}
                disabled={healing}
                data-testid="ig-webhook-banner-cta"
                className="bg-white text-[#EF9F27] px-3 py-1.5 rounded-md font-semibold text-xs whitespace-nowrap hover:bg-white/90 transition inline-flex items-center gap-1.5 disabled:opacity-60"
              >
                {healing
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Fixing…</>
                  : "Fix now →"}
              </button>
            )}
            {healError && (
              <a
                href="mailto:support@tadlo.ai"
                data-testid="ig-webhook-banner-support"
                className="bg-white text-[#EF9F27] px-3 py-1.5 rounded-md font-semibold text-xs whitespace-nowrap hover:bg-white/90 transition"
              >
                Contact support
              </a>
            )}
            <button
              type="button"
              onClick={() => setWebhookOff(false)}
              data-testid="ig-webhook-banner-dismiss"
              aria-label="Dismiss"
              className="p-1 rounded hover:bg-white/15 transition"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
